import { useState, useEffect, useCallback, useRef } from 'react';
import { useAgent } from '../../../context';
import { getAgentInstruction } from '../../../../../../services/agents';
import { updateInstruction, createInstruction } from '../../../../../../services/instructions';
import { addInstructionToAgent } from '../../../../../../services/agents';

const AUTOSAVE_DELAY = 1500;

/**
 * Hook para manejar la lógica de InstructionsPage
 * 
 * - Fetch de instrucciones del agente
 * - Autosave con debounce
 */
const useInstructionsPage = () => {
  const { agentId, loading: agentLoading } = useAgent();
  
  // Estado de instrucciones
  const [instruction, setInstruction] = useState('');
  const [instructionUuid, setInstructionUuid] = useState(null);
  const [hasExistingInstruction, setHasExistingInstruction] = useState(false);
  
  // Estados de loading y error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Estados de alertas
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  
  // Refs para autosave
  const saveTimeoutRef = useRef(null);
  const previousInstructionRef = useRef('');
  const hasUnsavedChangesRef = useRef(false);
  
  // Fetch inicial de instrucciones
  const fetchInstruction = useCallback(async () => {
    if (!agentId || agentLoading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAgentInstruction(agentId);
      const instructionData = response?.data?.body;
      
      if (instructionData) {
        const instructionText = instructionData.instruction || '';
        const uuid = instructionData.instructionUuid || instructionData.instruction_uuid || null;
        
        setInstruction(instructionText);
        setInstructionUuid(uuid);
        setHasExistingInstruction(!!uuid);
        previousInstructionRef.current = instructionText;
        hasUnsavedChangesRef.current = false;
      }
    } catch (err) {
      // Si es 404, el agente no tiene instrucción aún
      if (err.response?.status === 404 && err.response?.data?.msgCode === 'agent_instruction_not_found') {
        setInstruction('');
        setInstructionUuid(null);
        setHasExistingInstruction(false);
        previousInstructionRef.current = '';
        hasUnsavedChangesRef.current = false;
      } else {
        setError(err);
        console.error('Error fetching instruction:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [agentId, agentLoading]);
  
  // Cargar instrucciones cuando cambia el agentId
  useEffect(() => {
    if (agentId && !agentLoading) {
      fetchInstruction();
    }
  }, [agentId, agentLoading, fetchInstruction]);
  
  // Función de guardado
  const saveInstruction = useCallback(async () => {
    if (!agentId || isSaving) return;
    
    const currentInstruction = instruction.trim();
    
    // Si no hay cambios, no guardar
    if (currentInstruction === previousInstructionRef.current && hasExistingInstruction) {
      return;
    }
    
    setIsSaving(true);
    
    try {
      if (hasExistingInstruction && instructionUuid) {
        // Actualizar instrucción existente
        await updateInstruction(instructionUuid, {
          instruction_text: currentInstruction,
        });
      } else {
        // Crear nueva instrucción
        const response = await createInstruction({
          instruction_text: currentInstruction,
        });
        
        // Obtener el UUID de la nueva instrucción
        const newUuid = response?.data?.data?.instructionUuid || response?.data?.data?.instruction_uuid;
        
        if (newUuid) {
          // Asignar la instrucción al agente
          await addInstructionToAgent(agentId, newUuid);
          
          setInstructionUuid(newUuid);
          setHasExistingInstruction(true);
        }
      }
      
      previousInstructionRef.current = currentInstruction;
      hasUnsavedChangesRef.current = false;
      
      // Mostrar alerta de éxito
      setAlertMessage('Instruction successfully updated');
      setAlertType('success');
      setShowAlert(true);
    } catch (err) {
      console.error('Error saving instruction:', err);
      setAlertMessage('Error updating instruction');
      setAlertType('error');
      setShowAlert(true);
    } finally {
      setIsSaving(false);
    }
  }, [agentId, instruction, instructionUuid, hasExistingInstruction, isSaving]);
  
  // Autosave con debounce
  useEffect(() => {
    // Limpiar timeout anterior
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Solo guardar si hay cambios
    const currentInstruction = instruction.trim();
    if (hasUnsavedChangesRef.current && currentInstruction !== previousInstructionRef.current) {
      saveTimeoutRef.current = setTimeout(() => {
        saveInstruction();
      }, AUTOSAVE_DELAY);
    }
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [instruction, saveInstruction]);
  
  // Handler para cambio de instrucción
  const handleInstructionChange = useCallback((value) => {
    setInstruction(value);
    hasUnsavedChangesRef.current = true;
  }, []);
  
  // Handler para cerrar alerta
  const handleCloseAlert = useCallback(() => {
    setShowAlert(false);
    setAlertMessage('');
  }, []);
  
  return {
    // Estado
    instruction,
    loading,
    error,
    isSaving,
    
    // Alertas
    showAlert,
    alertMessage,
    alertType,
    
    // Handlers
    handleInstructionChange,
    handleCloseAlert,
  };
};

export default useInstructionsPage;

